import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, combineLatest, of, throwError } from 'rxjs';
import { map, switchMap, catchError, shareReplay, retry, timeout } from 'rxjs/operators';
import { GraphQLService } from './graphql.service';
import { AuthService } from './auth.service';
import { GatewayService } from './gateway.service';
import { RealtimeService } from './realtime.service';
import { StateManagementService } from './state-management.service';
import { TaskService } from './task.service';
import { ReportingService } from './reporting.service';
import { environment } from '../../../environments/environment';

/**
 * Unified Service Integration Layer
 * Provides seamless integration between REST, GraphQL, WebSocket, and caching
 */
@Injectable({ providedIn: 'root' })
export class IntegrationService {
  private readonly http = inject(HttpClient);
  private readonly graphql = inject(GraphQLService);
  private readonly auth = inject(AuthService);
  private readonly gateway = inject(GatewayService);
  private readonly realtime = inject(RealtimeService);
  private readonly state = inject(StateManagementService);
  private readonly taskService = inject(TaskService);
  private readonly reportingService = inject(ReportingService);

  // Service status signals
  private readonly _graphqlEnabled = signal<boolean>(true);
  private readonly _restEnabled = signal<boolean>(true);
  private readonly _websocketEnabled = signal<boolean>(true);
  private readonly _offlineMode = signal<boolean>(false);

  readonly graphqlEnabled = computed(() => this._graphqlEnabled());
  readonly restEnabled = computed(() => this._restEnabled());
  readonly websocketEnabled = computed(() => this._websocketEnabled());
  readonly offlineMode = computed(() => this._offlineMode());

  // Cache for offline support
  private readonly cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

  constructor() {
    this.initializeServiceStatus();
    this.setupOfflineDetection();
    this.setupRealtimeIntegration();
  }

  /**
   * Initialize service status monitoring
   */
  private initializeServiceStatus(): void {
    // Check GraphQL health
    this.gateway.checkGraphQLHealth().subscribe({
      next: (healthy) => this._graphqlEnabled.set(healthy),
      error: () => this._graphqlEnabled.set(false),
    });

    // Check REST health
    this.gateway.getHealth().subscribe({
      next: () => this._restEnabled.set(true),
      error: () => this._restEnabled.set(false),
    });

    // Check WebSocket status
    this.realtime.connected.subscribe({
      next: (connected) => this._websocketEnabled.set(connected),
    });
  }

  /**
   * Setup offline detection
   */
  private setupOfflineDetection(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this._offlineMode.set(false);
        this.initializeServiceStatus();
      });

      window.addEventListener('offline', () => {
        this._offlineMode.set(true);
      });

      // Initial check
      this._offlineMode.set(!navigator.onLine);
    }
  }

  /**
   * Setup realtime integration
   */
  private setupRealtimeIntegration(): void {
    if (this.auth.isAuthenticated()) {
      this.realtime.connect();
    }

    // Auto-connect when authenticated
    // Note: This would require watching auth state changes
  }

  /**
   * Unified query method - tries GraphQL first, falls back to REST
   */
  query<T = any>(
    operation: string,
    variables?: Record<string, any>,
    options?: {
      useGraphQL?: boolean;
      useRest?: boolean;
      useCache?: boolean;
      cacheKey?: string;
      cacheTTL?: number;
    }
  ): Observable<T> {
    const {
      useGraphQL = this._graphqlEnabled(),
      useRest = true,
      useCache = true,
      cacheKey = operation,
      cacheTTL = 60000, // 1 minute default
    } = options || {};

    // Check cache first
    if (useCache && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey)!;
      if (Date.now() - cached.timestamp < cached.ttl) {
        return of(cached.data);
      }
      this.cache.delete(cacheKey);
    }

    // Try GraphQL first if enabled
    if (useGraphQL && this._graphqlEnabled()) {
      return this.graphql.query<T>(operation, variables).pipe(
        map((response) => {
          if (response.errors && response.errors.length > 0) {
            throw new Error(response.errors[0].message);
          }
          return response.data as T;
        }),
        catchError((error) => {
          console.warn(`GraphQL query failed, falling back to REST:`, error);
          if (useRest) {
            return this.queryRest<T>(operation, variables);
          }
          return throwError(() => error);
        }),
        switchMap((data) => {
          // Cache result
          if (useCache) {
            this.cache.set(cacheKey, {
              data,
              timestamp: Date.now(),
              ttl: cacheTTL,
            });
          }
          return of(data);
        }),
        timeout(10000), // 10s timeout
        retry(2)
      );
    }

    // Fall back to REST
    if (useRest) {
      return this.queryRest<T>(operation, variables).pipe(
        switchMap((data) => {
          if (useCache) {
            this.cache.set(cacheKey, {
              data,
              timestamp: Date.now(),
              ttl: cacheTTL,
            });
          }
          return of(data);
        }),
        timeout(10000),
        retry(2)
      );
    }

    return throwError(() => new Error('No available query method'));
  }

  /**
   * REST query fallback
   */
  private queryRest<T>(operation: string, variables?: Record<string, any>): Observable<T> {
    // Parse operation to determine REST endpoint
    // This is a simplified example - you'd want more sophisticated parsing
    const endpoint = this.mapGraphQLOperationToRest(operation, variables);
    return this.http.get<T>(endpoint);
  }

  /**
   * Map GraphQL operation to REST endpoint
   */
  private mapGraphQLOperationToRest(operation: string, variables?: Record<string, any>): string {
    // Simplified mapping - extend as needed
    if (operation.includes('tasks')) {
      const id = variables?.id || variables?.taskId;
      return id
        ? `${environment.apiGatewayUrl}/api/v1/tasks/${id}`
        : `${environment.apiGatewayUrl}/api/v1/tasks`;
    }
    if (operation.includes('user') || operation.includes('users')) {
      const id = variables?.id || variables?.userId;
      return id
        ? `${environment.apiGatewayUrl}/api/v1/users/${id}`
        : `${environment.apiGatewayUrl}/api/v1/users`;
    }
    return `${environment.apiGatewayUrl}/api/v1/${operation}`;
  }

  /**
   * Unified mutation method
   */
  mutate<T = any>(
    operation: string,
    variables?: Record<string, any>,
    options?: {
      useGraphQL?: boolean;
      useRest?: boolean;
      invalidateCache?: string[];
    }
  ): Observable<T> {
    const {
      useGraphQL = this._graphqlEnabled(),
      useRest = true,
      invalidateCache = [],
    } = options || {};

    // Try GraphQL first if enabled
    if (useGraphQL && this._graphqlEnabled()) {
      return this.graphql.mutate<T>(operation, variables).pipe(
        map((response) => {
          if (response.errors && response.errors.length > 0) {
            throw new Error(response.errors[0].message);
          }
          return response.data as T;
        }),
        catchError((error) => {
          console.warn(`GraphQL mutation failed, falling back to REST:`, error);
          if (useRest) {
            return this.mutateRest<T>(operation, variables);
          }
          return throwError(() => error);
        }),
        switchMap((data) => {
          // Invalidate cache
          invalidateCache.forEach((key) => this.cache.delete(key));
          return of(data);
        }),
        timeout(15000), // 15s timeout for mutations
        retry(1)
      );
    }

    // Fall back to REST
    if (useRest) {
      return this.mutateRest<T>(operation, variables).pipe(
        switchMap((data) => {
          invalidateCache.forEach((key) => this.cache.delete(key));
          return of(data);
        }),
        timeout(15000),
        retry(1)
      );
    }

    return throwError(() => new Error('No available mutation method'));
  }

  /**
   * REST mutation fallback
   */
  private mutateRest<T>(operation: string, variables?: Record<string, any>): Observable<T> {
    const endpoint = this.mapGraphQLOperationToRest(operation, variables);
    const method = this.getRestMethod(operation);
    return this.http.request<T>(method, endpoint, { body: variables });
  }

  /**
   * Determine REST method from GraphQL operation
   */
  private getRestMethod(operation: string): string {
    if (operation.includes('create') || operation.includes('add')) return 'POST';
    if (operation.includes('update') || operation.includes('edit')) return 'PATCH';
    if (operation.includes('delete') || operation.includes('remove')) return 'DELETE';
    return 'POST'; // Default
  }

  /**
   * Subscribe to real-time updates
   */
  subscribe<T = any>(
    eventName: string,
    filter?: (event: any) => boolean
  ): Observable<T> {
    return this.realtime.on<T>(eventName).pipe(
      map((event) => {
        if (filter && !filter(event)) {
          return null as any;
        }
        return event;
      }),
      shareReplay(1)
    );
  }

  /**
   * Clear cache
   */
  clearCache(pattern?: string): void {
    if (pattern) {
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get cached data
   */
  getCached<T = any>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data as T;
    }
    return null;
  }

  /**
   * Service status summary
   */
  getServiceStatus(): Observable<{
    graphql: boolean;
    rest: boolean;
    websocket: boolean;
    offline: boolean;
  }> {
    return combineLatest([
      this.gateway.checkGraphQLHealth(),
      this.gateway.getHealth().pipe(
        map(() => true),
        catchError(() => of(false))
      ),
      this.realtime.connected.pipe(
        map((connected) => connected),
        catchError(() => of(false))
      ),
      of(this._offlineMode()),
    ]).pipe(
      map(([graphql, rest, websocket, offline]) => ({
        graphql,
        rest,
        websocket,
        offline,
      })),
      shareReplay(1)
    );
  }

  /**
   * Batch multiple queries
   */
  batchQuery<T = any>(
    queries: Array<{
      operation: string;
      variables?: Record<string, any>;
      options?: any;
    }>
  ): Observable<T[]> {
    const queryObservables = queries.map((q) =>
      this.query<T>(q.operation, q.variables, q.options)
    );
    return combineLatest(queryObservables);
  }
}

